import { useEffect, useRef, useState } from 'react';

/**
 * Brings an AI drawer's results into view once a generation lands.
 *
 * The results used to sit in a scroller of their own, sized to whatever height
 * the form above them left over. On a phone that was a strip one card tall, and
 * the drawer body around it would not scroll past it — so most of the answer
 * could only be read through a slot. They now flow in the body with the form,
 * which fixes that and moves them below the fold instead: the adviser taps the
 * button, the backdrop lifts, and nothing on screen has changed. So the body is
 * scrolled to the results' heading.
 *
 * Scrolls the drawer BODY rather than calling `scrollIntoView`, which walks every
 * scrollable ancestor and would move the page behind the drawer as well.
 */

/** Room above the heading, so it does not land flush against the drawer header. */
const HEADING_CLEARANCE = 12;

export const useRevealResults = () => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  // A counter rather than a flag: generating twice in a row must scroll twice.
  const [revealCount, setRevealCount] = useState(0);

  useEffect(() => {
    if (!revealCount) return;
    const body = bodyRef.current;
    const results = resultsRef.current;
    // No results rendered (an empty answer), or jsdom, which has no Element#scrollTo.
    if (!body || !results || typeof body.scrollTo !== 'function') return;
    const top =
      results.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop - HEADING_CLEARANCE;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    body.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [revealCount]);

  const reveal = () => setRevealCount(count => count + 1);

  return { bodyRef, resultsRef, reveal };
};
